import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, Card, Button, Spinner, Textarea, Badge } from "@/components/ui";
import { endpoints, apiError, api } from "@/lib/api";
import { Download, ArrowLeft, Save } from "lucide-react";

const FLAGS = [
  { id: "open", label: "Open", tone: "neutral" }, { id: "green", label: "Green", tone: "green" },
  { id: "amber", label: "Amber", tone: "yellow" }, { id: "red", label: "Red", tone: "red" }, { id: "done", label: "Done", tone: "navy" },
];

function Item({ dd, it, idx, onSaved }) {
  const [note, setNote] = useState(it.note || "");
  const [saving, setSaving] = useState(false);
  const save = async (patch) => {
    setSaving(true);
    try { const r = await endpoints.ddPatch(dd.id, it.key, patch); onSaved(r.data); } catch (ex) { alert(apiError(ex)); } finally { setSaving(false); }
  };
  return (
    <Card hover={false} data-testid={`dd-item-${it.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-display text-lg">{idx + 1}. {it.title}</div>
          <div className="text-[11.5px] text-white/40 mt-0.5 max-w-2xl">{it.help}</div>
        </div>
        <div className="flex gap-1" data-testid={`dd-flags-${it.key}`}>
          {FLAGS.map((f) => (
            <button key={f.id} onClick={() => save({ flag: f.id })} disabled={saving} data-testid={`dd-flag-${it.key}-${f.id}`}
              className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded border transition-colors ${it.flag === f.id ? "border-[#C5A059] text-gold bg-[#C5A059]/10" : "border-white/10 text-white/45 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-[12.5px] text-white/75 bg-white/[0.02] border border-white/5 rounded-md p-3 my-3" data-testid={`dd-auto-${it.key}`}>{it.auto_body}</pre>
      <div className="text-[10.5px] text-white/35 mb-3">Source: {it.source}</div>
      <Textarea rows={2} placeholder="Analyst note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`dd-note-${it.key}`} />
      <div className="flex justify-end mt-2">
        <Button size="sm" variant="outline" disabled={saving || note === (it.note || "")} onClick={() => save({ note })} data-testid={`dd-save-note-${it.key}`}>
          <Save size={12} /> {saving ? "Saving…" : "Save note"}
        </Button>
      </div>
    </Card>
  );
}

export default function DueDiligence() {
  const { id } = useParams();
  const [dd, setDd] = useState(null);
  const [err, setErr] = useState("");
  const [dl, setDl] = useState(false);
  useEffect(() => { endpoints.ddGet(id).then((r) => setDd(r.data)).catch((e) => setErr(apiError(e))); }, [id]);

  const downloadPdf = async () => {
    setDl(true);
    try {
      const r = await api.get(`/dd/${id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `CLARA_DD_${dd.company_name}.pdf`;
      a.click();
    } finally { setDl(false); }
  };

  if (err) return <div className="p-8 text-red-300" data-testid="dd-error">{err}</div>;
  if (!dd) return <div className="p-8"><Spinner /></div>;
  const counts = dd.items.reduce((m, i) => ({ ...m, [i.flag]: (m[i.flag] || 0) + 1 }), {});
  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-5">
      <Link to="/companies" className="inline-flex items-center gap-1 text-[12px] text-white/50 hover:text-gold" data-testid="dd-back"><ArrowLeft size={12} /> Companies</Link>
      <PageHeader eyebrow="Structured Due Diligence" title={<>{dd.company_name}</>}
        description={`Auto-populated from ${[...new Set([dd.provider, dd.cerved_source ? "Cerved" : null, "workspace data"].filter(Boolean))].join(" + ")} · created ${String(dd.created_at).slice(0, 10)}`}
        actions={<Button onClick={downloadPdf} disabled={dl} data-testid="dd-download-pdf"><Download size={13} /> {dl ? "Rendering…" : "Download PDF"}</Button>}
        testid="dd-page" />
      <div className="flex flex-wrap gap-2" data-testid="dd-summary">
        {FLAGS.map((f) => counts[f.id] ? <Badge key={f.id} tone={f.tone}>{f.label}: {counts[f.id]}</Badge> : null)}
      </div>
      {dd.items.map((it, i) => <Item key={it.key} dd={dd} it={it} idx={i} onSaved={setDd} />)}
    </div>
  );
}
